<script setup>
import {useBaseStore} from "~/stores/base";
import Modal from "~/components/Modal.vue";
// 引入swiper组件
import {Swiper, SwiperSlide} from "swiper/vue";
import SlideChooseCapsule from "~/components/wishlist/ModalWishlistCapsule/SlideChooseCapsule.vue";
import SlideCreateCapsule from "~/components/wishlist/ModalWishlistCapsule/SlideCreateCapsule.vue";
import {useWishlistStore} from "~/stores/wishlist";
import Drawer from "~/components/Drawer.vue";

const BNTOMessage = useNuxtApp().$BNTOMessage
const router = useRouter()
const route = useRoute()

/** emits **/
const emits = defineEmits(["close"])

/** props **/
const props = defineProps({
  open: {default: false},
  product: {default: {}},
})

/** data **/
const swiperInstance = ref(null);
const swiperSlideIndex = ref(0)
const likedLocal = ref(true) // 该modal打开时，产品必是已收藏状态，故初始化true
const operatedCapsuleIdList = ref([]) // 缓存刚刚加入过商品的胶囊列表，关闭弹窗后将这个列表的胶囊统一挪到最前面

/** computed **/
const isMobile = computed(() => useBaseStore().getterIsMobile)
const userInfo = computed(() => useBaseStore().getterUserInfo)
const title = computed(() => swiperSlideIndex.value === 0 ? "choose a capsule" : "create your capsule")
const capsuleCustom = computed(() => useWishlistStore().getterCapsuleCustom)

/** watch **/
watch(() => props.open, () => {
  // 开启后重置为true
  if (props.open) likedLocal.value = true
  if (!props.open) {
    /** 重排顺序 **/
    // 重排顺序规则是：
    //   1.	hasPo === 1 的池子 始终最前。
    //   2.	之后，按 capsuleId 列表中的顺序排列。
    //   3.	剩下的池子 保持原顺序。
    function reorderCapsules(capsules, capsuleIdList) {
      const hasPoCapsules = [];
      const listedCapsules = [];
      const remainingCapsules = [];

      const idSet = new Set(capsuleIdList);

      for (const capsule of capsules) {
        if (capsule.hasPo === 1) {
          hasPoCapsules.push(capsule);
        } else if (idSet.has(capsule.capsuleId)) {
          listedCapsules.push(capsule);
        } else {
          remainingCapsules.push(capsule);
        }
      }

      // 先排 hasPo=1 的，后排 capsuleIdList 里的，最后剩下的
      return [
        ...hasPoCapsules,
        ...capsuleIdList
          .map(id => listedCapsules.find(capsule => capsule.capsuleId === id))
          .filter(Boolean), // 过滤掉可能找不到的
        ...remainingCapsules
      ];
    }

    const capsules = capsuleCustom.value; // 总capsule列表
    const capsuleIdList = operatedCapsuleIdList.value; // 需要提前的capsuleIdList
    useWishlistStore().updateCapsuleCustom(reorderCapsules(capsules, capsuleIdList))
  }
})

/** methods **/
const handleClose = () => {
  emits("close", likedLocal.value)
}
const onSwiper = (swiper) => {
  swiperInstance.value = swiper; // 保存 Swiper 实例
};
const onSlideChange = (e) => {
  swiperSlideIndex.value = e.activeIndex
};
const handleCreateCapsule = () => {
  swiperInstance.value.slideTo(1)
}
const handleBack = () => {
  swiperInstance.value.slideTo(0)
}
const handleCapsuleCreated = async (newCapsule) => {
  if (swiperInstance.value) swiperInstance.value.slideTo(0)

  /** 将newCapsule插在hasPo的后面 **/
  const list = [...capsuleCustom.value];
  // 找最后一个 hasPo === 1 的项
  const index = [...list].reverse().findIndex(item => item.hasPo === 1);
  if (index !== -1) {
    const realIndex = list.length - index - 1; // 反转后需要修正回正向的下标
    list.splice(realIndex + 1, 0, newCapsule);
  } else {
    list.unshift(newCapsule);
  }
  useWishlistStore().updateCapsuleCustom(list);
}
const handleLikeChange = (liked) => {
  likedLocal.value = liked
}
const handleCapsuleAdded = (capsuleId) => {
  // 需要提前的胶囊列表
  const list = operatedCapsuleIdList.value.filter(id => id !== capsuleId)
  operatedCapsuleIdList.value = [capsuleId, ...list]
}

/** lifecycle **/

</script>

<template>
  <div class="modal-wishlist-capsule-mobile" v-if="isMobile">
    <Drawer
      :open="open"
      @close="handleClose"
      :btn-close-visible="false"
      :position="'bottom'"
      :border-radius="'8px 8px 0 0'"
      :z-index="4"
      :global-header-visible="false"
      :append-to-body="true"
    >
      <div class="modal-wishlist-capsule-mobile-content" v-if="open">
        <div class="drawer-header">
          <div class="drawer-header-left">
            <div class="back" v-if="swiperSlideIndex === 1" @click="handleBack"><img
              src="@/assets/img/icon-arrow-left.svg" alt="icon"></div>
          </div>
          <div class="title">{{ title }}</div>
          <div class="close" @click="handleClose">
            <img src="@/assets/img/icon-close-black.svg" alt="icon">
          </div>
        </div>
        <div class="drawer-body">
          <swiper
            :allow-touch-move="false"
            @swiper="onSwiper"
            @slideChange="onSlideChange"
          >
            <swiper-slide>
              <slide-choose-capsule @create="handleCreateCapsule" :product="product" @like-change="handleLikeChange" @capsule-add="handleCapsuleAdded"/>
            </swiper-slide>
            <swiper-slide>
              <slide-create-capsule @save="handleCapsuleCreated" :swiper-slide-index="swiperSlideIndex"/>
            </swiper-slide>
          </swiper>
        </div>
      </div>
    </Drawer>
  </div>
  <div class="modal-wishlist-capsule-desktop" v-else>
    <Modal
      :open="open"
      :title="title"
      :btn-back-visible="swiperSlideIndex === 1"
      @back="handleBack"
      :btnCloseVisible="true"
      @close="handleClose"
      :z-index="4"
      :append-to-body="true"
    >
      <div class="modal-wishlist-capsule-desktop-content">
        <swiper
          :allow-touch-move="false"
          @swiper="onSwiper"
          @slideChange="onSlideChange"
        >
          <swiper-slide>
            <slide-choose-capsule @create="handleCreateCapsule" :product="product" @like-change="handleLikeChange" @capsule-add="handleCapsuleAdded"/>
          </swiper-slide>
          <swiper-slide>
            <slide-create-capsule @save="handleCapsuleCreated" :swiper-slide-index="swiperSlideIndex"/>
          </swiper-slide>
        </swiper>
      </div>
    </Modal>
  </div>
</template>

<style lang="scss">
@import "src/assets/config";

.modal-wishlist-capsule-mobile-content {
  width: 100%;
  height: calc(100vh - $mobile-header-height - $mobile-safari-address-bar-height - 18px);
  padding-top: 14px;
  display: flex;
  flex-direction: column;

  .drawer-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 16px;

    .drawer-header-left {
      width: 40px;
      height: 40px;

      .back {
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
      }
    }

    .title {
      /* Eyebrow/medium */
      font-family: "TWK Lausanne";
      font-size: 14px;
      font-style: normal;
      font-weight: 650;
      line-height: 18px; /* 128.571% */
      letter-spacing: -0.056px;
      text-transform: uppercase;

      .title-content {
        display: flex;
        align-items: center;

        .icon {
          cursor: pointer;
          margin-left: 24px;
          margin-right: -54px;
          width: 40px;
          height: 40px;
          display: flex;
          justify-content: center;
          align-items: center;
        }
      }
    }

    .close {
      cursor: pointer;
      flex-shrink: 0;

      border: 1px solid $color-BNTO-beige-dark;

      width: 40px;
      height: 40px;
      display: flex;
      justify-content: center;
      align-items: center;

      img {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
      }
    }
  }

  .drawer-body {
    flex: 1;
    overflow-y: hidden;
    padding: 0 16px;
    margin-top: 16px;

    .swiper {
      height: 100%;

      .swiper-slide {
        height: 100%;
      }
    }
  }
}

.modal-wishlist-capsule-desktop-content {
  width: 500px;
}
</style>
